import React from 'react';

import { Button } from '@/design-system/button';
import { Popover, PopoverTrigger } from '@/design-system/popover';
import { FilterPopoverContent } from '@/components/FilterPopover';
import { SearchSelect } from '@/design-system/search-select';
import { Switch } from '@/design-system/switch';
import { UJP_FUEL_TYPES, type UjpFuelType } from '@/services/api/ujp';

import { UJP_COPY } from '../copy';

const c = UJP_COPY.masters;

export type UjpMasterFilterTab = 'vehicles' | 'energy' | 'vendors';

/** The applied / draft filter set of the Master UJP page (one shape for all tabs). */
export interface UjpMasterFilterValue {
  /** Vehicles + energy tabs: one fuel, '' = all. */
  fuelType: UjpFuelType | '';
  /** Vehicles tab: exact unit type from the distinct list, '' = all. */
  unitType: string;
  /** Vehicles + vendors tabs: also list deactivated rows. */
  showInactive: boolean;
}

export const EMPTY_UJP_MASTER_FILTERS: UjpMasterFilterValue = {
  fuelType: '',
  unitType: '',
  showInactive: false,
};

/** Which fields a tab shows — and therefore which ones count on its badge. */
const FIELDS_BY_TAB: Record<
  UjpMasterFilterTab,
  { fuel: boolean; unit: boolean; inactive: boolean }
> = {
  vehicles: { fuel: true, unit: true, inactive: true },
  energy: { fuel: true, unit: false, inactive: false },
  vendors: { fuel: false, unit: false, inactive: true },
};

/** Single source of truth for the "Filter (N)" badge, per tab. */
export const countActiveFilters = (
  tab: UjpMasterFilterTab,
  v: UjpMasterFilterValue
): number => {
  const f = FIELDS_BY_TAB[tab];
  return (
    (f.fuel && v.fuelType ? 1 : 0) +
    (f.unit && v.unitType ? 1 : 0) +
    (f.inactive && v.showInactive ? 1 : 0)
  );
};

const chipCls = (active: boolean) =>
  `rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors ${
    active
      ? 'border-accent text-accent'
      : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
  }`;

interface UjpMasterFilterPanelProps {
  tab: UjpMasterFilterTab;
  open: boolean;
  /** Controls the Radix popover. Opening seeds the draft in the parent. */
  onOpenChange: (open: boolean) => void;
  /** Number of applied filters — shown as an ink pill on the trigger. */
  activeFilterCount: number;
  value: UjpMasterFilterValue;
  onChange: (next: UjpMasterFilterValue) => void;
  onApply: () => void;
  onReset: () => void;
  /** Distinct unit types on file (vehicles tab), from `…/vehicles/unit-types`. */
  unitTypes: string[];
  unitTypesLoading?: boolean;
}

/**
 * "Filter (N)" popover of the Master UJP page, modelled on ShipmentFilterPanel:
 * the fields edit a local draft (seeded from the applied URL values when the
 * popover opens); Terapkan writes the draft, Reset clears everything — both in
 * one URL patch that also goes back to page 1. The fields shown follow the
 * active tab, the value shape stays one object so a tab switch is trivial.
 */
const UjpMasterFilterPanel: React.FC<UjpMasterFilterPanelProps> = ({
  tab,
  open,
  onOpenChange,
  activeFilterCount,
  value,
  onChange,
  onApply,
  onReset,
  unitTypes,
  unitTypesLoading = false,
}) => {
  const fields = FIELDS_BY_TAB[tab];
  const labelCls = 'mb-2 block text-[11px] font-medium text-text-sub-600';

  // Keep the applied unit type visible even when it has since vanished from
  // the distinct list (its last truck was renamed) — the user can still clear it.
  const unitOptions = (
    value.unitType && !unitTypes.includes(value.unitType)
      ? [value.unitType, ...unitTypes]
      : unitTypes
  ).map((u) => ({ value: u, label: u }));

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button tone="neutral" style="stroke" size="sm">
          <i className="ri-equalizer-2-line mr-1.5 text-[14px]" />
          {c.filter.trigger}
          {activeFilterCount > 0 && (
            <span className="ml-1.5 rounded-md bg-bg-strong-950 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-text-white-0">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <FilterPopoverContent
        align="end"
        className="w-[340px] p-0"
        aria-label={c.filter.title[tab]}
      >
        <div className="flex items-center justify-between border-b border-stroke-soft-200 px-4 py-3">
          <p className="text-sm font-semibold tracking-tight text-text-strong-950">
            {c.filter.title[tab]}
          </p>
          <button
            type="button"
            onClick={onReset}
            className="text-[11px] font-medium text-text-sub-600 transition-colors hover:text-accent"
          >
            {c.filter.reset}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto space-y-4 px-4 py-3">
          {fields.unit && (
            <div data-testid="ujp-master-filter-unit">
              <p className={labelCls}>{c.vehicles.unitType}</p>
              <SearchSelect
                options={unitOptions}
                value={value.unitType || undefined}
                onChange={(v) =>
                  onChange({ ...value, unitType: (v as string | undefined) ?? '' })
                }
                placeholder={c.vehicles.allUnitTypes}
                loading={unitTypesLoading}
                allowClear
                emptyMessage={c.vehicles.unitTypeEmpty}
              />
            </div>
          )}

          {fields.fuel && (
            <div>
              <p className={labelCls} id="ujp-master-filter-fuel-label">
                {c.energy.columns.fuel}
              </p>
              <div
                role="group"
                aria-labelledby="ujp-master-filter-fuel-label"
                className="flex flex-wrap gap-1"
              >
                {UJP_FUEL_TYPES.map((f) => {
                  const active = value.fuelType === f;
                  return (
                    <button
                      key={f}
                      type="button"
                      aria-pressed={active}
                      onClick={() =>
                        onChange({ ...value, fuelType: active ? '' : f })
                      }
                      className={chipCls(active)}
                    >
                      {c.fuelType[f]}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {fields.inactive && (
            <label className="flex items-center justify-between gap-3 text-[13px] text-text-strong-950">
              <span>{c.showInactive}</span>
              <Switch
                aria-label={c.showInactive}
                checked={value.showInactive}
                onCheckedChange={(on) => onChange({ ...value, showInactive: on })}
              />
            </label>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stroke-soft-200 px-4 py-3">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            {c.cancel}
          </Button>
          <Button size="sm" onClick={onApply}>
            {c.filter.apply}
          </Button>
        </div>
      </FilterPopoverContent>
    </Popover>
  );
};

export default UjpMasterFilterPanel;
